import React, { useMemo } from "react";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { fundService } from "../../services/fundService";
import { formatProductType } from "../../utils/enumFormatter";
import { formatHandlerCode } from "../../utils/namesFormater";
import { colorByFundId } from "../../utils/colorEnumFormatter";
import ChipSelector, { ChipSelectorOption } from "./ChipSelector";


interface FundChipSelectorProps {
  selectedFundIds: number[];
  onSelectedFundsChange: (fundIds: number[]) => void;
  handlerFilter?: string[];
  productTypeFilter?: number[];
  placeholder?: string;
  label?: string;
  maxHeight?: number;
  variant?: 'outlined' | 'filled';
  size?: 'small' | 'medium';
  showFundInfo?: boolean;
  useCustomColors?: boolean;
}

const FundChipSelector: React.FC<FundChipSelectorProps> = ({
  selectedFundIds,
  onSelectedFundsChange,
  handlerFilter,
  productTypeFilter,
  placeholder,
  label = "הוסף קרנות לניתוח",
  maxHeight = 300,
  variant = 'filled',
  size = 'small',
  showFundInfo = true,
  useCustomColors = true,
}) => {
  // קבלת כל הקרנות המפולטרות
  const filteredFunds = useMemo(() => {
    const results: FundDataBasic[] = [];

    // אם יש גם productType וגם handler
    if (productTypeFilter?.length && handlerFilter?.length) {
      for (const productType of productTypeFilter) {
        for (const handler of handlerFilter) {
          results.push(
            ...fundService.getFundsFromCacheByCriteria({
              productType,
              handler_code: handler,
            })
          );
        }
      }
    }
    // רק productType
    else if (productTypeFilter?.length) {
      for (const productType of productTypeFilter) {
        results.push(
          ...fundService.getFundsFromCacheByCriteria({ productType })
        );
      }
    }
    // רק handler
    else if (handlerFilter?.length) {
      for (const handler of handlerFilter) {
        results.push(
          ...fundService.getFundsFromCacheByCriteria({ handler_code: handler })
        );
      }
    }
    // אם אין פילטרים - החזר array ריק כדי לא להציג הכל
    else {
      return [];
    }

    // הסרת כפילויות לפי fund_id
    const uniqueFunds = new Map<number, FundDataBasic>();
    results.forEach((fund) => {
      if (!uniqueFunds.has(fund.fund_id)) {
        uniqueFunds.set(fund.fund_id, fund);
      }
    });

    return Array.from(uniqueFunds.values());
  }, [handlerFilter, productTypeFilter]);

  // המרה לפורמט של ChipSelectorOption
  const fundOptions = useMemo((): ChipSelectorOption<number>[] => {
    return filteredFunds.map((fund) => ({
      value: fund.fund_id,
      label: `${fund.fund_name} (${fund.fund_id})`,
      subtitle: showFundInfo
        ? `${formatHandlerCode(String(fund.managing_corporation_legal_id))} • ${fund.sub_specialization || 'כללי'}`
        : undefined,
    }));
  }, [filteredFunds, showFundInfo]);

  // יצירת placeholder דינמי
  const dynamicPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;

    const parts: string[] = [];

    if (productTypeFilter?.length) {
      const names = productTypeFilter.map(formatProductType).join(", ");
      parts.push(names);
    }

    if (handlerFilter?.length) {
      const handlers = handlerFilter.map(formatHandlerCode).join(", ");
      parts.push(`של ${handlers}`);
    }

    return `חפש והוסף קרן${parts.length ? ` (${parts.join(" ")})` : ""}...`;
  }, [productTypeFilter, handlerFilter, placeholder]);

  // פונקציית צבע מותאמת אישית
  const getCustomChipColor = useCustomColors 
    ? (fundId: number) => colorByFundId(fundId)
    : undefined;

  return (
    <ChipSelector<number>
      options={fundOptions}
      selectedValues={selectedFundIds}
      onChange={onSelectedFundsChange}
      placeholder={dynamicPlaceholder}
      label={label}
      maxHeight={maxHeight}
      variant={variant}
      size={size}
      showSubtitle={showFundInfo}
      customChipColor={getCustomChipColor}
      noOptionsText="לא נמצאו קרנות מתאימות"
    />
  );
};

export default FundChipSelector;